import jsPDF from 'jspdf';
// eslint-disable-next-line import/no-named-as-default
import autoTable, { CellInput, RowInput } from 'jspdf-autotable';
import { formatCurrency } from '@shared/commonFunctions';
import { CreditReportGridColumns } from './components/CreditReport/CreditReportGridColumns';
import {
  chunkPaymentPattern,
  sortTradelinesByClosedIndicatorStatus,
  TRADE_LINE_HEADERS,
} from './components/CreditReport/TradeLines';
import {
  FieldType,
  ICollections,
  IEmploymentInformation,
  IPublicRecords,
  ITradeLine,
} from './components/CreditReport/interfaces/interfaces';
import { PUBLIC_RECORDS_HEADERS } from './components/CreditReport/PublicRecords';
import { COLLECTIONS_HEADERS } from './components/CreditReport/Collections';
import { buildReportSummaryFields, REPORT_SUMMARY_SECTION_BOUNDS } from './components/CreditReport/ProfileReportSummary';
import { buildAutoSummarySections } from './components/CreditReport/AutoSummaryProfile';
import { EMPLOYMENT_COLUMNS } from './components/CreditReport/EmploymentDetails';
import { BUREAU_ADDRESS_TEXT } from './components/CreditReport/BureauAddress';
import { EBureauType, ICreditReportData } from './interfaces';

interface jsPDFWithAutoTable extends jsPDF {
  lastAutoTable: {
    finalY: number;
  };
}

interface GenerateCreditReportPdfParams {
  creditReportData: ICreditReportData;
  bureauType: EBureauType;
}

export const getFinalY = (pdf: jsPDF): number => (pdf as jsPDFWithAutoTable).lastAutoTable.finalY;

export const toAutoTableColumns = (gridColumns: { field: string; headerName?: string }[]) =>
  gridColumns.map((col) => ({
    header: col.headerName ?? col.field,
    dataKey: col.field,
  }));

export const toAutoTableRows = (rows: Record<string, CellInput>[], columnCount: number): RowInput[] => {
  if (!rows.length) {
    return [
      [
        {
          content: 'No Rows',
          colSpan: columnCount,
          styles: {
            halign: 'center',
            valign: 'middle',
            textColor: [140, 140, 140],
            cellPadding: 16,
            fontStyle: 'normal',
          },
        },
      ],
    ];
  }

  return rows.map((row) => ({ ...row }));
};


export const generateCreditReportPdf = ({ creditReportData, bureauType }: GenerateCreditReportPdfParams) => {
  const pdf = new jsPDF('p', 'pt', 'a4');
  const marginX = 40;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const rightX = pageWidth - marginX;
  let y = 50;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - 40) {
      pdf.addPage();
      y = 50;
    }
  };

  const summary = creditReportData.creditReportSummary;

  const personalInfo = summary?.personalInformation;
  let ficoScoreType = '';
  const leftBlockTop = y;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text(personalInfo?.name ?? '', marginX, y);
  y += 16;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  (summary?.ficoDetails ?? []).forEach((fico) => {
    pdf.text(`${fico.name} - ${fico.score}/${fico.scoreType} ${fico.score}`, marginX, y);
    ficoScoreType = fico.scoreType;
    y += 14;
  });

  if (summary?.mlaInformation) {
    pdf.text(String(summary.mlaInformation), marginX, y);
    y += 14;
  }
  const leftBlockBottom = y;

  let titleY = leftBlockTop + 4;
  pdf.setFont('times', 'normal');
  pdf.setFontSize(22);
  pdf.text(`${String(bureauType)} Report`, rightX, titleY, { align: 'right' });
  titleY += 15;

  let rightY = titleY + 28;
  const addressLines = BUREAU_ADDRESS_TEXT[bureauType] ?? [];
  addressLines.forEach((line, idx) => {
    pdf.setFont('helvetica', idx === 0 ? 'bold' : 'normal');
    pdf.setFontSize(idx === 0 ? 10 : 9);
    pdf.text(line, rightX, rightY, { align: 'right' });
    rightY += 13;
  });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(`Date Reported: ${summary?.dateReported ?? ''}`, rightX, rightY, { align: 'right' });
  rightY += 13;

  y = Math.max(leftBlockBottom, rightY) + 24;

  // Personal Information
  ensureSpace(24);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Personal Information', marginX, y);
  y += 10;

  const personalRows: RowInput[] = [];
  const shadedRows = new Set<number>();

  personalRows.push([
    { content: 'Name', styles: { fontStyle: 'bold' } },
    personalInfo?.name ?? '',
    { content: 'Date of Birth', styles: { fontStyle: 'bold' } },
    personalInfo?.dataOfBirth ?? '',
  ]);

  (personalInfo?.addresses ?? []).forEach((address) => {
    const startIdx = personalRows.length;
    personalRows.push([
      { content: 'Address', styles: { fontStyle: 'bold' } },
      { content: address.address ?? '', colSpan: 3 },
    ]);
    personalRows.push([
      { content: 'Reported On', styles: { fontStyle: 'bold' } },
      { content: address.reportedDate ?? '', colSpan: 3 },
    ]);
    shadedRows.add(startIdx);
    shadedRows.add(startIdx + 1);

    personalRows.push([{ content: '', colSpan: 4, styles: { minCellHeight: 6 } }]);
  });

  const akaIdx = personalRows.length;
  personalRows.push([
    { content: 'AKA', styles: { fontStyle: 'bold' } },
    (personalInfo?.aka ?? []).join(', '),
    { content: `SSN: ${personalInfo?.ssn ?? ''}`, colSpan: 2, styles: { fontStyle: 'bold' } },
  ]);
  shadedRows.add(akaIdx);

  autoTable(pdf, {
    startY: y,
    body: personalRows,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 6, lineWidth: 0 },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 155 },
      2: { cellWidth: 90 },
      3: { cellWidth: 190 },
    },
    margin: { left: marginX, right: marginX },
    didParseCell: (data) => {
      if (data.section === 'body' && shadedRows.has(data.row.index)) {
        data.cell.styles.fillColor = [211, 211, 211];
      }
    },
  });

  y = getFinalY(pdf) + 24;

  // Employment
  const employmentInfo = (summary?.employmentInformation ?? []) as IEmploymentInformation[];
  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Employment', marginX, y);
  y += 10;

  const employmentRows = toAutoTableRows(employmentInfo as unknown as Record<string, CellInput>[], EMPLOYMENT_COLUMNS.length);

  autoTable(pdf, {
    startY: y,
    columns: EMPLOYMENT_COLUMNS,
    body: employmentRows,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [230, 230, 230], textColor: 20 },
    margin: { left: marginX, right: marginX },
  });

  y = getFinalY(pdf) + 40;

  // Auto Summary Profile
  const autoSummary = summary?.autoSummaryProfile;
  if (autoSummary) {
    const { section1, section2, section3 } = buildAutoSummarySections(autoSummary);
    const columns = [section1, section2, section3];

    const contentWidth = pageWidth - marginX * 2;
    const boxPadding = 12;
    const colGap = 10;
    const colWidth = (contentWidth - boxPadding * 2 - colGap * (columns.length - 1)) / columns.length;
    const labelWidth = colWidth - 26;

    ensureSpace(30);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text('Auto Summary Profile', marginX, y);
    y += 16;

    const lineHeight = 11;
    const itemGap = 10;
    const wrappedColumns = columns.map((items) =>
      items.map((item) => {
        const labelLines: string[] =
          pdf.setFont('helvetica', 'bold') && pdf.setFontSize(8.5) ? pdf.splitTextToSize(item.label, labelWidth) : [];
        return { labelLines, value: String(item.value) };
      })
    );

    const colContentHeights = wrappedColumns.map((col) =>
      col.reduce((sum, item) => sum + item.labelLines.length * lineHeight + itemGap, 0)
    );
    const boxContentHeight = Math.max(...colContentHeights);
    const boxHeight = boxContentHeight + boxPadding * 2 - itemGap + 4;

    ensureSpace(boxHeight + 10);

    const boxTopY = y;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.75);
    pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

    wrappedColumns.forEach((col, colIndex) => {
      const colX = marginX + boxPadding + colIndex * (colWidth + colGap);
      let colY = boxTopY + boxPadding + lineHeight;

      col.forEach((item) => {
        const blockHeight = item.labelLines.length * lineHeight;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8.5);
        pdf.text(item.labelLines, colX, colY);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        const valueX = colX + labelWidth + 4;
        const valueY = colY + (blockHeight - lineHeight) / 2;
        pdf.text(item.value, valueX, valueY);

        colY += blockHeight + itemGap;
      });
    });

    y = boxTopY + boxHeight + 20;
  }

  // Auto Summary
  const autoSummaryDetails = summary?.autoSummaryProfile?.autoSummaryProfileDetails ?? [];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Auto Summary', marginX, y);
  y += 10;

  const autoSummaryColumns = toAutoTableColumns(CreditReportGridColumns('Auto Summary'));
  const autoSummaryRows = toAutoTableRows(
    autoSummaryDetails as unknown as Record<string, CellInput>[],
    autoSummaryColumns.length
  );

  autoTable(pdf, {
    startY: y,
    columns: autoSummaryColumns,
    body: autoSummaryRows,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [230, 230, 230], textColor: 20 },
    margin: { left: marginX, right: marginX },
  });

  y = getFinalY(pdf) + 40;

  // Student Loan
  const studentLoanDetails = summary?.studentLoanSummaryProfile?.studentLoanSummaryProfileDetails ?? [];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Student Loan', marginX, y);
  y += 10;

  const studentLoanColumns = toAutoTableColumns(CreditReportGridColumns('Student Loan'));
  const studentLoanRows = toAutoTableRows(
    studentLoanDetails as unknown as Record<string, CellInput>[],
    studentLoanColumns.length
  );

  autoTable(pdf, {
    startY: y,
    columns: studentLoanColumns,
    body: studentLoanRows,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [230, 230, 230], textColor: 20 },
    margin: { left: marginX, right: marginX },
  });

  y = getFinalY(pdf) + 40;

  // Report Summary
  const reportSummaryFields = buildReportSummaryFields(summary?.profileReportSummary);
  const reportSummaryKeys = Object.keys(reportSummaryFields);

  const columns = REPORT_SUMMARY_SECTION_BOUNDS.map(([start, bound]) =>
    reportSummaryKeys.slice(start, bound).map((key) => {
      const field = reportSummaryFields[key];
      const displayValue = field?.type === FieldType.currency ? formatCurrency(field?.value) : String(field?.value ?? '');
      return { label: key, value: displayValue };
    })
  );

  const contentWidth = pageWidth - marginX * 2;
  const boxPadding = 12;
  const colGap = 10;
  const colWidth = (contentWidth - boxPadding * 2 - colGap * (columns.length - 1)) / columns.length;
  const labelWidth = colWidth - 55;

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Report Summary', marginX, y);
  y += 16;

  const lineHeight = 11;
  const itemGap = 8;
  const wrappedColumns = columns.map((col) =>
    col.map((item) => ({
      labelLines: pdf.setFont('helvetica', 'bold') && pdf.setFontSize(8.5) ? pdf.splitTextToSize(item.label, labelWidth) : [],
      value: item.value,
    }))
  );

  const colContentHeights = wrappedColumns.map((col) =>
    col.reduce((sum, item) => sum + Math.max(item.labelLines.length, 1) * lineHeight + itemGap, 0)
  );
  const boxContentHeight = Math.max(...colContentHeights);
  const boxHeight = boxContentHeight + boxPadding * 2 - itemGap + 4;

  ensureSpace(boxHeight + 10);

  const boxTopY = y;
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.75);
  pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

  wrappedColumns.forEach((col, colIndex) => {
    const colX = marginX + boxPadding + colIndex * (colWidth + colGap);
    let colY = boxTopY + boxPadding + lineHeight;

    col.forEach((item) => {
      const blockHeight = Math.max(item.labelLines.length, 1) * lineHeight;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.text(item.labelLines, colX, colY);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      const valueX = colX + labelWidth + 4;
      const valueY = colY + (blockHeight - lineHeight) / 2;
      pdf.text(item.value, valueX, valueY);

      colY += blockHeight + itemGap;
    });
  });

  y = boxTopY + boxHeight + 20;

  // Collections
  const collectionsData = (summary?.collections ?? []) as ICollections[];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Collections', marginX, y);
  y += 10;

  const collectionsRows: RowInput[] =
    collectionsData.length > 0
      ? collectionsData.map((data) => {
          const col1 = [data.memberNumber ?? '', data.creditor ?? '', data.status ?? '', data.narrative ?? ''].join('\n');

          const col2 = [data.industryCode ?? '', data.accountNumber ?? ''].join('\n');

          const col3 = [data.dateReported ?? '', data.balanceDate ?? '', data.dateClosed ?? ''].join('\n');

          const col4 = [data.amount ? formatCurrency(data.amount) : '', data.balance ? formatCurrency(data.balance) : ''].join(
            '\n'
          );

          return [col1, col2, col3, col4];
        })
      : [
          [
            {
              content: 'No Rows',
              colSpan: COLLECTIONS_HEADERS.length,
              styles: {
                halign: 'center' as const,
                valign: 'middle' as const,
                textColor: [140, 140, 140] as [number, number, number],
                cellPadding: 16,
              },
            },
          ],
        ];

  const collectionsColRatios = [150, 90, 90, 80];
  const collectionsRatioSum = collectionsColRatios.reduce((sum, r) => sum + r, 0);
  const collectionsColWidths = collectionsColRatios.map((r) => (r / collectionsRatioSum) * contentWidth);

  autoTable(pdf, {
    startY: y,
    head: [COLLECTIONS_HEADERS],
    body: collectionsRows,
    styles: { fontSize: 7, cellPadding: 4, valign: 'top' },
    headStyles: { fontSize: 7, fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
    tableWidth: contentWidth,
    columnStyles: {
      0: { cellWidth: collectionsColWidths[0] },
      1: { cellWidth: collectionsColWidths[1] },
      2: { cellWidth: collectionsColWidths[2] },
      3: { cellWidth: collectionsColWidths[3] },
    },
    margin: { left: marginX, right: marginX },
  });

  y = getFinalY(pdf) + 40;

  // Score Cards
  const scoreCards = summary?.scoreCards ?? [];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Score Cards', marginX, y);
  y += 16;

  const labelColWidth = 60;
  const valueColWidth = contentWidth - boxPadding * 2 - labelColWidth;

  if (!scoreCards.length) {
    const boxHeight = 30;
    ensureSpace(boxHeight + 10);
    const boxTopY = y;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.75);
    pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text('No Records found', marginX + boxPadding, boxTopY + boxHeight / 2 + 3);

    y = boxTopY + boxHeight + 20;
  } else {
    const entries = scoreCards.map((score) => {
      const scorecardValue = `${score.scoreType ?? ''} ${score.scoreCard ?? ''}`.trim();
      const scorecardLines = pdf.splitTextToSize(scorecardValue, valueColWidth);

      const reasonLines = (score.scoreCardReasons ?? []).map((reason) =>
        `${reason.code ?? ''} ${reason.codeReason ?? ''}`.trim()
      );

      const wrappedReasonLines = reasonLines.flatMap((line) => pdf.splitTextToSize(line, valueColWidth));

      const entryHeight = Math.max(scorecardLines.length, 1) * lineHeight + Math.max(wrappedReasonLines.length, 1) * lineHeight;

      return { scorecardLines, reasonLines: wrappedReasonLines, entryHeight };
    });

    const boxContentHeight = entries.reduce((sum, e) => sum + e.entryHeight, 0);
    const boxHeight = boxContentHeight + boxPadding * 2;

    ensureSpace(Math.min(boxHeight, pageHeight - 100) + 10);
    const boxTopY = y;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.75);
    pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

    let entryY = boxTopY + boxPadding + 9;
    const valueX = marginX + boxPadding + labelColWidth;

    entries.forEach((entry) => {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);

      pdf.text('Scorecard', marginX + boxPadding, entryY);
      pdf.text(entry.scorecardLines, valueX, entryY);
      entryY += Math.max(entry.scorecardLines.length, 1) * lineHeight;

      pdf.text('Reason', marginX + boxPadding, entryY);
      pdf.text(entry.reasonLines.length ? entry.reasonLines : [''], valueX, entryY);
      entryY += Math.max(entry.reasonLines.length, 1) * lineHeight;
    });

    y = boxTopY + boxHeight + 20;
  }

  // Credit Report Messages
  const creditReportMessages = summary?.creditReportMessages ?? [];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Credit Report Messages', marginX, y);
  y += 16;

  const messagesBoxPadding = 12;
  const messagesLabelColWidth = 75;
  const messagesValueColWidth = contentWidth - messagesBoxPadding * 2 - messagesLabelColWidth;
  const messagesLineHeight = 13;

  if (!creditReportMessages.length) {
    const boxHeight = 30;
    ensureSpace(boxHeight + 10);
    const boxTopY = y;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.75);
    pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text('No Records found', marginX + messagesBoxPadding, boxTopY + boxHeight / 2 + 3);

    y = boxTopY + boxHeight + 20;
  } else {
    const messageEntries = creditReportMessages.map((msg) => {
      const reportedDateLines = pdf.splitTextToSize(msg?.reportedDate ?? '', messagesValueColWidth);
      const messageLines = pdf.splitTextToSize(msg?.message ?? '', messagesValueColWidth);

      const entryHeight =
        Math.max(reportedDateLines.length, 1) * messagesLineHeight + Math.max(messageLines.length, 1) * messagesLineHeight;

      return { reportedDateLines, messageLines, entryHeight };
    });

    const boxContentHeight = messageEntries.reduce((sum, e) => sum + e.entryHeight, 0);
    const boxHeight = boxContentHeight + messagesBoxPadding * 2;

    ensureSpace(Math.min(boxHeight, pageHeight - 100) + 10);
    const boxTopY = y;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.75);
    pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

    let entryY = boxTopY + messagesBoxPadding + 9;
    const valueX = marginX + messagesBoxPadding + messagesLabelColWidth;

    messageEntries.forEach((entry) => {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);

      pdf.text('Reported Date', marginX + messagesBoxPadding, entryY);
      pdf.text(entry.reportedDateLines.length ? entry.reportedDateLines : [''], valueX, entryY);
      entryY += Math.max(entry.reportedDateLines.length, 1) * messagesLineHeight;

      pdf.text('Messages', marginX + messagesBoxPadding, entryY);
      pdf.text(entry.messageLines.length ? entry.messageLines : [''], valueX, entryY);
      entryY += Math.max(entry.messageLines.length, 1) * messagesLineHeight;
    });

    y = boxTopY + boxHeight + 20;
  }

  // Public Records
  const publicRecordsData = (summary?.publicRecords ?? []) as IPublicRecords[];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Public Records', marginX, y);
  y += 10;

  const publicRecordsRows: RowInput[] =
    publicRecordsData.length > 0
      ? publicRecordsData.map((data) => {
          const col1 = [
            `${data.court ? `${data.court}/` : ''} ${data.courtCode ?? ''}`,
            data.recordType ?? '',
            data.courtType ?? '',
            '',
          ].join('\n');

          const col2 = [data.statusDate ?? '', data.statusDate ?? '', data.accDesignator ?? '', data.docket ?? ''].join('\n');

          const col3 = [data.referenceNumber ?? '', data.platiffName ?? '', '', ''].join('\n');

          const col4 = [
            data.amount ? formatCurrency(data.amount) : '',
            data.assetAmount ? formatCurrency(data.assetAmount) : '',
            data.liabilityAmount ? formatCurrency(data.liabilityAmount) : '',
            data.originalDate ?? '',
          ].join('\n');

          return [col1, col2, col3, col4];
        })
      : [
          [
            {
              content: 'No Rows',
              colSpan: PUBLIC_RECORDS_HEADERS.length,
              styles: {
                halign: 'center' as const,
                valign: 'middle' as const,
                textColor: [140, 140, 140] as [number, number, number],
                cellPadding: 16,
              },
            },
          ],
        ];

  autoTable(pdf, {
    startY: y,
    head: [PUBLIC_RECORDS_HEADERS],
    body: publicRecordsRows,
    styles: { fontSize: 7, cellPadding: 4, valign: 'top' },
    headStyles: { fontSize: 7, fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 150 },
      1: { cellWidth: 90 },
      2: { cellWidth: 90 },
      3: { cellWidth: 80 },
    },
    margin: { left: marginX, right: marginX },
  });

  y = getFinalY(pdf) + 40;

  // Trade Lines
  const rawTradeLines = (summary?.tradeLines ?? []) as ITradeLine[];
  const tradeLines =
    bureauType === EBureauType.Transunion && rawTradeLines.length
      ? sortTradelinesByClosedIndicatorStatus([...rawTradeLines], ['Open', 'Closed'])
      : rawTradeLines;

  if (tradeLines.length) {
    ensureSpace(30);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text('Trade Lines', marginX, y);
    y += 10;

    const tradeLineRows: RowInput[] = tradeLines.map((data) => {
      const col1 = [
        `${data.firmName ? `${data.firmName}/` : ''} ${data.firmID ?? ''}`,
        data.accountNumber ?? '',
        `${data.kob ? `${data.kob}/` : ''} ${data.tradeType ?? ''}`,
        data.status ?? '',
        data.phone ?? '',
        data.narativeDescription?.toUpperCase() ?? '',
      ]
        .filter(Boolean)
        .join('\n');

      const col2 = [data.dateOpened ?? '', data.dateReported ?? '', data.accountType ?? ''].join('\n');

      const col3 = [
        data.creditLimit ? formatCurrency(data.creditLimit) : '',
        data.highCredit ? formatCurrency(data.highCredit) : '',
        data.chargeOff ? formatCurrency(data.chargeOff) : '',
        data.dateLastPayment ?? data.dateLastActivity ?? '',
      ].join('\n');

      const col4 = [
        data.balance ? formatCurrency(data.balance) : '',
        data.pastDue ? formatCurrency(data.pastDue) : '',
        data.originalLoanValue ? formatCurrency(data.originalLoanValue) : '',
        data.closedIndicator ?? '',
      ].join('\n');

      const col5 = [
        data.monthlyPayment ? formatCurrency(data.monthlyPayment) : '',
        data.monthlyRepayment ?? '',
        data.dateClosed ?? '',
        data.owner ?? '',
      ].join('\n');

      const patternLines = chunkPaymentPattern(data.paymentPattern ?? '');
      const col6 = [
        `${data.past30Days ?? ''}  ${data.past60Days ?? ''}  ${data.past90Days ?? ''}`,
        ...patternLines,
        data.terms ?? '',
      ].join('\n');

      return [col1, col2, col3, col4, col5, col6];
    });

    const tradeLineColRatios = [120, 55, 65, 65, 65, 75];
    const ratioSum = tradeLineColRatios.reduce((sum, r) => sum + r, 0);
    const tradeLineColWidths = tradeLineColRatios.map((r) => (r / ratioSum) * contentWidth);

    autoTable(pdf, {
      startY: y,
      head: [TRADE_LINE_HEADERS],
      body: tradeLineRows,
      styles: { fontSize: 9, cellPadding: 4, valign: 'top' },
      headStyles: { fontSize: 9, fillColor: [230, 230, 230], textColor: 20, fontStyle: 'bold' },
      tableWidth: contentWidth,
      columnStyles: {
        0: { cellWidth: tradeLineColWidths[0] },
        1: { cellWidth: tradeLineColWidths[1] },
        2: { cellWidth: tradeLineColWidths[2] },
        3: { cellWidth: tradeLineColWidths[3] },
        4: { cellWidth: tradeLineColWidths[4] },
        5: { cellWidth: tradeLineColWidths[5] },
      },
      margin: { left: marginX, right: marginX },
    });

    y = getFinalY(pdf) + 40;
  }

  // Inquiries
  const inquiriesData = summary?.inquiries ?? [];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Inquiries', marginX, y);
  y += 10;

  const inquiriesColumns = toAutoTableColumns(CreditReportGridColumns('Inquiries'));
  const inquiriesRows = toAutoTableRows(inquiriesData as unknown as Record<string, CellInput>[], inquiriesColumns.length);

  autoTable(pdf, {
    startY: y,
    columns: inquiriesColumns,
    body: inquiriesRows,
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [230, 230, 230], textColor: 20 },
    margin: { left: marginX, right: marginX },
  });

  y = getFinalY(pdf) + 40;

  //  Warning Messages
  const warningMessages = summary?.warningMessage?.messages ?? [];

  ensureSpace(30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Warning Messages', marginX, y);
  y += 16;

  const warningBoxPadding = 10;
  const warningLineHeight = 12;
  const warningTextWidth = contentWidth - warningBoxPadding * 2;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);

  if (!warningMessages.length) {
    const boxHeight = 26;
    ensureSpace(boxHeight + 10);
    const boxTopY = y;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.75);
    pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

    pdf.text('No Records Found', marginX + warningBoxPadding, boxTopY + boxHeight / 2 + 3);

    y = boxTopY + boxHeight + 20;
  } else {
    const wrappedLines = warningMessages.flatMap((msg) => pdf.splitTextToSize(msg, warningTextWidth));

    const boxHeight = wrappedLines.length * warningLineHeight + warningBoxPadding * 2 - 2;

    ensureSpace(Math.min(boxHeight, pageHeight - 100) + 10);
    const boxTopY = y;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.75);
    pdf.roundedRect(marginX, boxTopY, contentWidth, boxHeight, 3, 3);

    pdf.text(wrappedLines, marginX + warningBoxPadding, boxTopY + warningBoxPadding + 8);

    y = boxTopY + boxHeight + 20;
  }

  pdf.save(`${bureauType}-${ficoScoreType}-credit-report.pdf`);
};
